
import { computed, defineComponent, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ConfirmOrder from '@/views/dashboard/service/_components/confirm-order/confirm-order.vue'
import OrderPrice from '@/views/dashboard/service/_components/order-price/order-price.vue'
import { ServiceCommonService } from '@/views/dashboard/service/service-common.service'
import { useRoute, useRouter } from 'vue-router'
import StepList from '@/views/_components/step-list/step-list.vue'
import PayError from '@/views/dashboard/service/_components/pay-error/pay-error.vue'
import PayNative from '@/views/dashboard/service/_components/pay-native/pay-native.vue'
import OfficePay from '@/views/dashboard/service/_components/office-pay/office-pay.vue'
import PayDetails from '@/views/dashboard/service/_components/pay-details/pay-details.vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  GoodsDetails,
} from '@/views/dashboard/service/service-common.service'
import { OrderAction } from '@/views/dashboard/service/_enum/order-action'
import ServiceEnum from '@/views/dashboard/service/_components/service-enum/service-enum.vue'
import CmTag from '@/views/dashboard/_components/cm-tag.vue'
import SubscribeDetailsTable
  from '@/views/dashboard/service/_components/subscribe-details-table/subscribe-details-table.vue'
import FormulaDetail from '@/views/dashboard/service/_components/formula-detail/formula-detail.vue'
import RenewalService from '@/views/dashboard/service/subscribe-details/_components/renewal-service.vue'
import BuyMoreOptions from '@/views/dashboard/service/buy-more/_components/buy-more-options.vue'
import {
  BuyMoreService,
  CalcPriceRes,
  CycleServiceItem,
  ServiceList
} from '@/views/dashboard/service/buy-more/buy-more.service'
import { PayType } from '@/views/dashboard/service/_enum/pay-type'
import { formatTimeStamp } from '@/utils/time'
import { DateKindLabel } from '@/views/dashboard/service/_enum/date-kind'
import { BrandConfig, useBrandCustomStore } from '@/services/brand-custom.service'
import { ResponseError } from '@/services/http'
import { errorKeyMap } from '@/common/error/error-code'

export default defineComponent({
  name: 'BuyMore',
  props: {
    isPrivate: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  components: {
    BuyMoreOptions,
    RenewalService,
    FormulaDetail,
    SubscribeDetailsTable,
    CmTag,
    ServiceEnum,
    OfficePay,
    ConfirmOrder,
    StepList,
    PayNative,
    OrderPrice,
    PayError,
    PayDetails,
  },
  setup (props, { emit }) {
    const { t, locale } = useI18n()
    const injector = useReflectiveInjector([ServiceCommonService, BuyMoreService])
    const serviceCommonService = injector.get(ServiceCommonService)
    const service = injector.get(BuyMoreService)

    const router = useRouter()
    const route = useRoute()
    const stepList = computed(() => {
      void locale.value
      return [
        t('buyMore.stepOptions'),
        t('buyMore.stepConfirm'),
        t('buyMore.stepPay'),
      ]
    })
    const routeQuery = route.query
    const currentStepIndex = ref(0)
    const confirmOrderNode = ref()
    const dialogErrorPayVisible = ref<boolean>(false)
    const dialogOrderVisible = ref<boolean>(false)
    const cycleCount = ref(1)
    const serviceList = ref<ServiceList[]>([])
    const serviceListSelected = ref<ServiceList[]>([])
    const queryParams = reactive({
      price_ids: [],
      is_cancel: 0,
      action: OrderAction.expansion,
      pay_type: 1,
    })

    const payParams = reactive({
      product_id: ''
    })

    const viewModel = reactive({
      priceData: {} as CalcPriceRes,
      detailData: {} as GoodsDetails,
      detailOrderStatus: '',
    })

    function updateCalcPrice () {
      if (!queryParams.price_ids.length) {
        viewModel.priceData = {} as CalcPriceRes
        return
      }

      service.calcPrice(queryParams).then(response => {
        viewModel.priceData = response
      })
    }

    function payCurrent () {
      queryParams.is_cancel = 1
      service.createOrder(queryParams).then((res) => {
        payParams.product_id = res.product_id
        dialogErrorPayVisible.value = false
        confirmNext()
      }).catch((error: ResponseError) => {
        const ret = Number((error as any)?.responseBody?.ret)
        const msg = String((error as any)?.responseBody?.msg || (error as any)?.message || '')
        if (ret === errorKeyMap.HAD_PAYING_ORDER.ret || msg.includes('未支付订单')) {
          dialogErrorPayVisible.value = true
        }
      })
    }

    function handleConfirmOrder (data) {
      queryParams.pay_type = data.payType

      queryParams.is_cancel = 0

      service.createOrder(queryParams).then((res) => {
        if (res.err_code === 0) {
          payParams.product_id = res.product_id
          confirmNext()
        } else {
          dialogErrorPayVisible.value = true
          payParams.product_id = res.product_id
        }
      }).catch((error: ResponseError) => {
        const ret = Number((error as any)?.responseBody?.ret)
        const msg = String((error as any)?.responseBody?.msg || (error as any)?.message || '')
        if (ret === errorKeyMap.HAD_PAYING_ORDER.ret || msg.includes('未支付订单')) {
          dialogErrorPayVisible.value = true
        }
      })
    }

    function toHistory () {
      if (payParams.product_id) {
        dialogErrorPayVisible.value = false
        dialogOrderVisible.value = true
        return
      }
      router.push(`/dashboard/order-history`)
    }

    async function handleNextStep () {
      if (currentStepIndex.value === 1) {
        confirmOrderNode.value.checkConfirm()
      } else {
        confirmNext()
      }
    }

    function confirmNext () {
      currentStepIndex.value++
    }

    async function changeRenewalServiceSelect (price_ids: number[], list) {
      queryParams.price_ids = price_ids
      serviceListSelected.value = list
      updateCalcPrice()
    }

    function updateServiceList () {
      service.getServiceList().then((res) => {
        serviceList.value = res.service_list
      })
    }

    function handleOfficePayConfirm () {
      emit('close')
    }

    updateServiceList()

    return {
      DateKindLabel,
      PayType,
      brandConfig: useBrandCustomStore().brandConfig as BrandConfig,
      stepList,
      cycleCount,
      serviceList,
      serviceListSelected,
      confirmOrderNode,
      currentStepIndex,
      queryParams,
      payParams,
      viewModel,
      dialogErrorPayVisible,
      dialogOrderVisible,
      toHistory,
      formatTimeStamp,
      payCurrent,
      changeRenewalServiceSelect,
      handleConfirmOrder,
      handleNextStep,
      updateCalcPrice,
      handleOfficePayConfirm,
    }
  }
})
